import React, { useState, useEffect, useCallback } from 'react';
import { getBaseUrl } from './config';
import 'bulma/css/bulma.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';
import TableSidebar from './components/TableSidebar';
import DataTable from './components/DataTable';
import Footer from './components/Footer';
import Navbar from './components/Navbar';
import Operations from './components/Operations';
import Joystick from './components/Joystick';
import LoginTest from './components/LoginTest';
import ServerUrl from './components/ServerUrl';

function App() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, recordId: null, recordData: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editModal, setEditModal] = useState({ isOpen: false, recordId: null, recordData: null });
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [deleteTableModal, setDeleteTableModal] = useState({ isOpen: false, tableName: '' });
  const [deleteTableInput, setDeleteTableInput] = useState('');
  const [deleteTableLoading, setDeleteTableLoading] = useState(false);
  const [addModal, setAddModal] = useState({ isOpen: false, tableName: '' });
  const [addFormData, setAddFormData] = useState({});
  const [addLoading, setAddLoading] = useState(false);
  const [deleteColumnModal, setDeleteColumnModal] = useState({ isOpen: false, tableName: '', columnName: '' });
  const [deleteColumnInput, setDeleteColumnInput] = useState('');
  const [deleteColumnLoading, setDeleteColumnLoading] = useState(false);
  const [addColumnModal, setAddColumnModal] = useState({ isOpen: false, tableName: '' });
  const [addColumnFormData, setAddColumnFormData] = useState({ columnName: '', columnType: 'text', defaultValue: '' });
  const [addColumnLoading, setAddColumnLoading] = useState(false);
  const [healthCheck, setHealthCheck] = useState({ isHealthy: null, loading: true });
  const [activeView, setActiveView] = useState('tables');
  const [joystick, setJoystick] = useState({ activeDelay: null, activeStatus: null });
  const [joystickDraftDelay, setJoystickDraftDelay] = useState('');
  const [joystickDraftStatus, setJoystickDraftStatus] = useState('');
  const [authTables, setAuthTables] = useState([]);
  const [authTableConfig, setAuthTableConfig] = useState(null);

  const detectAuthTables = useCallback(async (tableList) => {
    if (!tableList || !Array.isArray(tableList)) {
      setAuthTables([]);
      return;
    }
    const detected = [];
    for (const table of tableList) {
      try {
        // Fetch just 1 row to check columns
        const response = await fetch(`${getBaseUrl()}/api/${table}?per-page=1`);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            const columns = Object.keys(data[0]);
            const hasUserOrEmail = columns.some(c => ['username', 'email'].includes(c.toLowerCase()));
            const hasPassword = columns.some(c => c.toLowerCase() === 'password');
            if (hasUserOrEmail && hasPassword) {
              detected.push(table);
            }
          }
        }
      } catch (err) {
        console.error(`Error detecting auth for table ${table}:`, err);
      }
    }
    setAuthTables(detected);
  }, []);

  const fetchTables = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getBaseUrl()}/ops/tables`);
      if (!response.ok) {
        throw new Error('Failed to fetch tables');
      }
      const data = await response.json();
      setTables(data);
      setError(null);

      // Detect auth tables
      detectAuthTables(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [detectAuthTables]);

  const fetchJoystick = useCallback(async () => {
    try {
      const response = await fetch(`${getBaseUrl()}/joystick`);
      if (!response.ok) {
        throw new Error('Failed to fetch joystick state');
      }
      const data = await response.json();
      setJoystick({
        activeDelay: data.delay ?? null,
        activeStatus: data.status ?? null
      });
      setJoystickDraftDelay(data.delay == null ? '' : String(data.delay));
      setJoystickDraftStatus(data.status == null ? '' : String(data.status));
    } catch (err) {
      console.error('Error fetching joystick state:', err);
      // Keep default values on error
    }
  }, []);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== 'injee_joystick_refresh') {
        return;
      }

      fetchJoystick();
    };

    const handleWindowFocus = () => {
      const hasValues = (joystick.activeDelay != null && joystick.activeDelay !== 0) ||
                        (joystick.activeStatus != null && joystick.activeStatus !== 200 && joystick.activeStatus !== '');
      if (activeView === 'joystick' || hasValues) {
        fetchJoystick();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const hasValues = (joystick.activeDelay != null && joystick.activeDelay !== 0) ||
                          (joystick.activeStatus != null && joystick.activeStatus !== 200 && joystick.activeStatus !== '');
        if (activeView === 'joystick' || hasValues) {
          fetchJoystick();
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchJoystick, joystick, activeView]);

  const handleAuthClick = (tableName) => {
    // Fetch schema for the selected auth table to know which fields to use
    const fetchSchemaAndNavigate = async () => {
      try {
        const response = await fetch(`${getBaseUrl()}/api/${tableName}?per-page=1`);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            const columns = Object.keys(data[0]);
            const userField = columns.find(c => ['username', 'email'].includes(c.toLowerCase()));
            const passwordField = columns.find(c => c.toLowerCase() === 'password');

            setAuthTableConfig({
              tableName,
              userField,
              passwordField
            });
            setActiveView('login-test');
          }
        }
      } catch (err) {
        console.error('Error fetching auth table schema:', err);
      }
    };

    fetchSchemaAndNavigate();
  };

  const checkHealth = useCallback(async () => {
    setHealthCheck({ isHealthy: null, loading: true });
    try {
      const response = await fetch(`${getBaseUrl()}/ops/health`);
      if (!response.ok) {
        throw new Error('Health check failed');
      }
      const data = await response.json();
      if (data.health === 'ok') {
        setHealthCheck({ isHealthy: true, loading: false });
        // If health check passes, fetch tables and joystick state
        fetchTables();
        fetchJoystick();
      } else {
        setHealthCheck({ isHealthy: false, loading: false });
      }
    } catch (err) {
      setHealthCheck({ isHealthy: false, loading: false });
    }
  }, [fetchTables, fetchJoystick]);

  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  const handleViewChange = (view) => {
    // When navigating to the Tables view, always re-fetch the tables list so
    // any tables created from other views (e.g. Operations) show up immediately.
    if (view === 'tables') {
      fetchTables();
    }
    setActiveView(view);
  };

  const fetchTableData = async (tableName, page = 1, pageSize = perPage, search = searchTerm, sort = sortColumn, order = sortOrder) => {
    setDataLoading(true);
    setDataError(null);

    // Reset pagination when selecting a new table
    if (tableName !== selectedTable) {
      setSelectedTable(tableName);
      setCurrentPage(1);
      page = 1;
    }

    try {
      // Build URL with pagination, search, and sorting parameters
      let url = `${getBaseUrl()}/api/${tableName}?per-page=${pageSize}&page=${page}`;
      if (search && search.trim()) {
        url += `&q=${encodeURIComponent(search.trim())}`;
      }
      if (sort && sort.trim()) {
        url += `&sort=${encodeURIComponent(sort.trim())}&order=${order}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch data for table: ${tableName}`);
      }
      const data = await response.json();

      // Handle array response (which is what our API returns)
      if (Array.isArray(data)) {
        setTableData(data);
        setCurrentPage(page);

        // Simple logic: if we get exactly the page size, there might be more pages
        // If we get less than page size, this is likely the last page
        if (data.length === pageSize) {
          // Check if there's a next page by trying to fetch it
          try {
            const nextPageResponse = await fetch(`${getBaseUrl()}/api/${tableName}?per-page=${pageSize}&page=${page + 1}`);
            if (nextPageResponse.ok) {
              const nextPageData = await nextPageResponse.json();
              if (Array.isArray(nextPageData) && nextPageData.length > 0) {
                // There is a next page with data
                setTotalPages(page + 1); // At least one more page
                setTotalRecords((page * pageSize) + nextPageData.length);
              } else {
                // Next page is empty, current page is the last
                setTotalPages(page);
                setTotalRecords(page * pageSize);
              }
            } else {
              // Can't fetch next page, assume current is last
              setTotalPages(page);
              setTotalRecords(page * pageSize);
            }
          } catch {
            // Error checking next page, assume current is last
            setTotalPages(page);
            setTotalRecords(page * pageSize);
          }
        } else {
          // Got less than page size, this is the last page
          setTotalPages(page);
          setTotalRecords((page - 1) * pageSize + data.length);
        }
      } else {
        // Handle structured response with metadata (if API changes in future)
        const rows = Array.isArray(data.data)
          ? data.data
          : Array.isArray(data.results)
            ? data.results
            : [];

        setTableData(rows);

        const total = typeof data.total === 'number' ? data.total : rows.length;
        setTotalRecords(total);
        setTotalPages(pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1);
        setCurrentPage(page);
      }
    } catch (err) {
      setDataError(err.message);
      setTableData([]);
      setTotalRecords(0);
      setTotalPages(1);
      setCurrentPage(1);
    } finally {
      setDataLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (selectedTable && newPage >= 1 && newPage <= totalPages) {
      fetchTableData(selectedTable, newPage, perPage);
    }
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    if (selectedTable) {
      fetchTableData(selectedTable, 1, newPerPage);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (selectedTable) {
      setSearchTerm(searchInput);
      setCurrentPage(1);
      fetchTableData(selectedTable, 1, perPage, searchInput);
    }
  };

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    if (selectedTable) {
      setCurrentPage(1);
      fetchTableData(selectedTable, 1, perPage, '');
    }
  };

  const handleDeleteClick = (recordId, recordData) => {
    setDeleteModal({
      isOpen: true,
      recordId: recordId,
      recordData: recordData
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.recordId || !selectedTable) return;

    setDeleteLoading(true);
    try {
      const response = await fetch(`${getBaseUrl()}/api/${selectedTable}/${deleteModal.recordId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`Failed to delete record: ${response.status}`);
      }

      // Close modal and refresh data
      setDeleteModal({ isOpen: false, recordId: null, recordData: null });

      // Refresh the current page data
      fetchTableData(selectedTable, currentPage, perPage, searchTerm);

    } catch (err) {
      setDataError(`Delete failed: ${err.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, recordId: null, recordData: null });
  };

  // Helper function to infer field type from value
  const inferFieldType = (key, value) => {
    if (typeof value === 'boolean') {
      return 'boolean';
    }
    if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) {
      return 'date';
    }
    if (typeof value === 'number') {
      return 'number';
    }
    if (typeof value === 'string' && value.length > 100) {
      return 'textarea';
    }
    return 'text';
  };

  const handleEditClick = (recordId, recordData) => {
    setEditModal({
      isOpen: true,
      recordId: recordId,
      recordData: recordData
    });
    // Initialize form data with current record values
    setEditFormData({ ...recordData });
  };

  const handleEditFormChange = (fieldName, value) => {
    setEditFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editModal.recordId || !selectedTable) return;

    setEditLoading(true);
    try {
      // Create payload with only changed fields (excluding id and timestamps)
      const payload = {};
      Object.keys(editFormData).forEach(key => {
        if (key !== 'id' && !key.includes('_at') && editFormData[key] !== editModal.recordData[key]) {
          payload[key] = editFormData[key];
        }
      });

      const response = await fetch(`${getBaseUrl()}/api/${selectedTable}/${editModal.recordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Failed to update record: ${response.status}`);
      }

      // Close modal and refresh data
      setEditModal({ isOpen: false, recordId: null, recordData: null });
      setEditFormData({});

      // Refresh the current page data
      fetchTableData(selectedTable, currentPage, perPage, searchTerm);

    } catch (err) {
      setDataError(`Update failed: ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditModal({ isOpen: false, recordId: null, recordData: null });
    setEditFormData({});
  };

  const handleSort = (columnName) => {
    let newOrder = 'asc';

    // If clicking the same column, toggle the order
    if (sortColumn === columnName) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }

    setSortColumn(columnName);
    setSortOrder(newOrder);

    if (selectedTable) {
      setCurrentPage(1);
      fetchTableData(selectedTable, 1, perPage, searchTerm, columnName, newOrder);
    }
  };

  const handleDeleteTableClick = (tableName) => {
    setDeleteTableModal({ isOpen: true, tableName: tableName });
    setDeleteTableInput('');
  };

  const handleDeleteTableConfirm = async () => {
    if (deleteTableInput !== deleteTableModal.tableName) {
      return; // Don't proceed if table name doesn't match
    }

    setDeleteTableLoading(true);
    try {
      const response = await fetch(`${getBaseUrl()}/ops/delete-table/${deleteTableModal.tableName}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`Failed to delete table: ${response.status}`);
      }

      // Close modal and refresh tables list
      setDeleteTableModal({ isOpen: false, tableName: '' });
      setDeleteTableInput('');

      // Clear selected table if it was the one deleted
      if (selectedTable === deleteTableModal.tableName) {
        setSelectedTable(null);
        setTableData([]);
      }

      // Refresh tables list
      const tablesResponse = await fetch(`${getBaseUrl()}/ops/tables`);
      if (tablesResponse.ok) {
        const tablesData = await tablesResponse.json();
        setTables(tablesData);
      }

    } catch (err) {
      setError(`Delete table failed: ${err.message}`);
    } finally {
      setDeleteTableLoading(false);
    }
  };

  const handleDeleteTableCancel = () => {
    setDeleteTableModal({ isOpen: false, tableName: '' });
    setDeleteTableInput('');
  };

  const handleAddRecordClick = (tableName) => {
    setAddModal({ isOpen: true, tableName: tableName });
    // Initialize form data with empty values based on existing table structure
    if (tableData.length > 0) {
      const sampleRecord = tableData[0];
      const initialData = {};
      Object.keys(sampleRecord).forEach(key => {
        if (key !== 'id' && !key.includes('_at')) {
          const fieldType = inferFieldType(key, sampleRecord[key]);
          if (fieldType === 'boolean') {
            initialData[key] = false;
          } else if (fieldType === 'number') {
            initialData[key] = 0;
          } else {
            initialData[key] = '';
          }
        }
      });
      setAddFormData(initialData);
    } else {
      setAddFormData({});
    }
  };

  const handleAddFormChange = (fieldName, value) => {
    setAddFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTable) return;

    setAddLoading(true);
    try {
      const response = await fetch(`${getBaseUrl()}/api/${selectedTable}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(addFormData)
      });

      if (!response.ok) {
        throw new Error(`Failed to create record: ${response.status}`);
      }

      // Close modal and refresh data
      setAddModal({ isOpen: false, tableName: '' });
      setAddFormData({});

      // Refresh the current page data
      fetchTableData(selectedTable, currentPage, perPage, searchTerm);

    } catch (err) {
      setDataError(`Create failed: ${err.message}`);
    } finally {
      setAddLoading(false);
    }
  };

  const handleAddCancel = () => {
    setAddModal({ isOpen: false, tableName: '' });
    setAddFormData({});
  };

  const handleDeleteColumnClick = (tableName, columnName) => {
    setDeleteColumnModal({ isOpen: true, tableName: tableName, columnName: columnName });
    setDeleteColumnInput('');
  };

  const handleDeleteColumnConfirm = async () => {
    if (deleteColumnInput !== deleteColumnModal.columnName) {
      return; // Don't proceed if column name doesn't match
    }

    setDeleteColumnLoading(true);
    try {
      const response = await fetch(`${getBaseUrl()}/ops/remove-column/${deleteColumnModal.tableName}/${deleteColumnModal.columnName}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`Failed to delete column: ${response.status}`);
      }

      // Close modal and refresh data
      setDeleteColumnModal({ isOpen: false, tableName: '', columnName: '' });
      setDeleteColumnInput('');

      // Refresh the current page data to show updated table structure
      fetchTableData(selectedTable, currentPage, perPage, searchTerm);

    } catch (err) {
      setDataError(`Delete column failed: ${err.message}`);
    } finally {
      setDeleteColumnLoading(false);
    }
  };

  const handleDeleteColumnCancel = () => {
    setDeleteColumnModal({ isOpen: false, tableName: '', columnName: '' });
    setDeleteColumnInput('');
  };

  const handleAddColumnClick = (tableName) => {
    setAddColumnModal({ isOpen: true, tableName: tableName });
    setAddColumnFormData({ columnName: '', columnType: 'text', defaultValue: '' });
  };

  const handleAddColumnFormChange = (fieldName, value) => {
    setAddColumnFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleAddColumnSubmit = async (e) => {
    e.preventDefault();
    if (!addColumnFormData.columnName.trim() || !selectedTable) return;

    setAddColumnLoading(true);
    try {
      // Prepare the payload based on column type
      let payload = {};

      if (addColumnFormData.defaultValue.trim()) {
        // Convert default value based on column type
        let defaultValue = addColumnFormData.defaultValue.trim();

        switch (addColumnFormData.columnType) {
          case 'number':
            defaultValue = parseFloat(defaultValue) || 0;
            break;
          case 'boolean':
            defaultValue = defaultValue.toLowerCase() === 'true';
            break;
          case 'date':
            // Keep as string for date
            break;
          default:
            // Keep as string for text and textarea
            break;
        }

        payload.default = defaultValue;
      }

      const response = await fetch(`${getBaseUrl()}/ops/add-column/${selectedTable}/${addColumnFormData.columnName.trim()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Failed to add column: ${response.status}`);
      }

      // Close modal and refresh data
      setAddColumnModal({ isOpen: false, tableName: '' });
      setAddColumnFormData({ columnName: '', columnType: 'text', defaultValue: '' });

      // Refresh the current page data to show updated table structure
      fetchTableData(selectedTable, currentPage, perPage, searchTerm);

    } catch (err) {
      setDataError(`Add column failed: ${err.message}`);
    } finally {
      setAddColumnLoading(false);
    }
  };

  const handleAddColumnCancel = () => {
    setAddColumnModal({ isOpen: false, tableName: '' });
    setAddColumnFormData({ columnName: '', columnType: 'text', defaultValue: '' });
  };



  // Show health check loading or error message
  if (healthCheck.loading) {
    return (
      <>
        <Navbar activeView={activeView} onViewChange={handleViewChange} joystick={joystick} />
        <div className="container is-fluid">
          <div className="section">
            <div className="has-text-centered">
              <button className="button is-loading is-static">Checking injee server...</button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (healthCheck.isHealthy === false) {
    return (
      <>
        <Navbar activeView={activeView} onViewChange={handleViewChange} joystick={joystick} />
        <div className="container is-fluid">
          <div className="section">
            <div className="notification is-warning">
              <h1 className="title">Server Not Running</h1>
              <p>
                Looks like you don't have <a href="https://injee.codeberg.page/" target="_blank" rel="noopener noreferrer">injee</a> running.
                You may like to know how to <a href="https://injee.codeberg.page/docs/#/run_injee" target="_blank" rel="noopener noreferrer">run it</a>.
              </p>
              <p>
                Trying to reach <code>{getBaseUrl()}</code>.
              </p>
              <div className="field is-grouped">
                <div className="control">
                  <button className="button is-primary" onClick={checkHealth}>Retry</button>
                </div>
              </div>
            </div>
            <ServerUrl onSave={checkHealth} />
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar activeView={activeView} onViewChange={handleViewChange} joystick={joystick} />
      {activeView === 'tables' && (
        <div className="container is-fluid" style={{ paddingTop: '2rem' }}>
          <div className="columns is-fullheight">
            <TableSidebar
              tables={tables}
              loading={loading}
              error={error}
              selectedTable={selectedTable}
              onTableSelect={fetchTableData}
              onTablesUpdate={fetchTables}
              authTables={authTables}
              onAuthClick={handleAuthClick}
            />
            <DataTable
              selectedTable={selectedTable}
              tableData={tableData}
              dataLoading={dataLoading}
              dataError={dataError}
              searchInput={searchInput}
              searchTerm={searchTerm}
              onSearchInputChange={handleSearchInputChange}
              onSearch={handleSearch}
              onClearSearch={clearSearch}
              currentPage={currentPage}
              perPage={perPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              sortColumn={sortColumn}
              sortOrder={sortOrder}
              onSort={handleSort}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
              onEditClick={handleEditClick}
              onDeleteClick={handleDeleteClick}
              onDeleteTable={handleDeleteTableClick}
              onAddRecord={handleAddRecordClick}
              onDeleteColumn={handleDeleteColumnClick}
              onAddColumn={handleAddColumnClick}
            />
          </div>
        </div>
      )}

      {activeView === 'operations' && <Operations onTablesUpdate={fetchTables} />}

      {activeView === 'login-test' && authTableConfig && (
        <LoginTest
          config={authTableConfig}
          onBack={() => handleViewChange('tables')}
        />
      )}

      {activeView === 'joystick' && (
        <Joystick
          draftDelay={joystickDraftDelay}
          setDraftDelay={setJoystickDraftDelay}
          draftStatus={joystickDraftStatus}
          setDraftStatus={setJoystickDraftStatus}
          onJoystickRefresh={fetchJoystick}
        />
      )}


      {/* Delete Confirmation Modal */}
      <div className={`modal ${deleteModal.isOpen ? 'is-active' : ''}`}>
        <div className="modal-background" onClick={handleDeleteCancel}></div>
        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Confirm Delete</p>
            <button
              className="delete"
              aria-label="close"
              onClick={handleDeleteCancel}
            ></button>
          </header>
          <section className="modal-card-body">
            <p>Are you sure you want to delete this record?</p>
            {deleteModal.recordData && (
              <div className="content">
                <p><strong>Record details:</strong></p>
                <ul>
                  {Object.entries(deleteModal.recordData).slice(0, 3).map(([key, value]) => (
                    <li key={key}>
                      <strong>{key}:</strong> {value !== null && value !== undefined ? String(value) : 'N/A'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="notification is-warning is-light">
              <strong>Warning:</strong> This action cannot be undone.
            </div>
          </section>
          <footer className="modal-card-foot">
            <button
              className={`button is-danger ${deleteLoading ? 'is-loading' : ''}`}
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
            >
              Delete
            </button>
            <button
              className="button"
              onClick={handleDeleteCancel}
              disabled={deleteLoading}
            >
              Cancel
            </button>
          </footer>
        </div>
      </div>

      {/* Edit Modal */}
      <div className={`modal ${editModal.isOpen ? 'is-active' : ''}`}>
        <div className="modal-background" onClick={handleEditCancel}></div>
        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Edit Record</p>
            <button
              className="delete"
              aria-label="close"
              onClick={handleEditCancel}
            ></button>
          </header>
          <form onSubmit={handleEditSubmit}>
            <section className="modal-card-body">
              {editModal.recordData && Object.entries(editModal.recordData).map(([key, value]) => {
                // Skip id and timestamp fields
                if (key === 'id' || key.includes('_at')) {
                  return null;
                }

                const fieldType = inferFieldType(key, value);
                const currentValue = editFormData[key] || '';

                return (
                  <div key={key} className="field">
                    <label className="label">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</label>
                    <div className="control">
                      {fieldType === 'boolean' ? (
                        <div className="field">
                          <div className="switch">
                            <input
                              id={`switch-${key}`}
                              type="checkbox"
                              checked={currentValue}
                              onChange={(e) => handleEditFormChange(key, e.target.checked)}
                            />
                            <label htmlFor={`switch-${key}`}>
                              {currentValue ? 'True' : 'False'}
                            </label>
                          </div>
                        </div>
                      ) : fieldType === 'textarea' ? (
                        <textarea
                          className="textarea"
                          value={currentValue}
                          onChange={(e) => handleEditFormChange(key, e.target.value)}
                          rows="4"
                        />
                      ) : fieldType === 'date' ? (
                        <input
                          className="input"
                          type="datetime-local"
                          value={currentValue ? new Date(currentValue).toISOString().slice(0, 16) : ''}
                          onChange={(e) => handleEditFormChange(key, e.target.value)}
                        />
                      ) : fieldType === 'number' ? (
                        <input
                          className="input"
                          type="number"
                          value={currentValue}
                          onChange={(e) => handleEditFormChange(key, parseFloat(e.target.value) || 0)}
                        />
                      ) : (
                        <input
                          className="input"
                          type="text"
                          value={currentValue}
                          onChange={(e) => handleEditFormChange(key, e.target.value)}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </section>
            <footer className="modal-card-foot">
              <button
                className={`button is-success ${editLoading ? 'is-loading' : ''}`}
                type="submit"
                disabled={editLoading}
              >
                Save Changes
              </button>
              <button
                className="button"
                type="button"
                onClick={handleEditCancel}
                disabled={editLoading}
              >
                Cancel
              </button>
            </footer>
          </form>
        </div>
      </div>

      {/* Delete Table Confirmation Modal */}
      <div className={`modal ${deleteTableModal.isOpen ? 'is-active' : ''}`}>
        <div className="modal-background" onClick={handleDeleteTableCancel}></div>
        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Delete Table</p>
            <button
              className="delete"
              aria-label="close"
              onClick={handleDeleteTableCancel}
            ></button>
          </header>
          <section className="modal-card-body">
            <p>Are you sure you want to delete the entire table <strong>"{deleteTableModal.tableName}"</strong>?</p>
            <div className="notification is-danger is-light">
              <strong>Warning:</strong> This will permanently delete the table and all its data. This action cannot be undone.
            </div>
            <div className="field">
              <label className="label">
                Type the table name <strong>"{deleteTableModal.tableName}"</strong> to confirm:
              </label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  placeholder={`Type "${deleteTableModal.tableName}" here`}
                  value={deleteTableInput}
                  onChange={(e) => setDeleteTableInput(e.target.value)}
                />
              </div>
            </div>
          </section>
          <footer className="modal-card-foot">
            <button
              className={`button is-danger ${deleteTableLoading ? 'is-loading' : ''}`}
              onClick={handleDeleteTableConfirm}
              disabled={deleteTableLoading || deleteTableInput !== deleteTableModal.tableName}
            >
              Delete Table
            </button>
            <button
              className="button"
              onClick={handleDeleteTableCancel}
              disabled={deleteTableLoading}
            >
              Cancel
            </button>
          </footer>
        </div>
      </div>

      {/* Add Record Modal */}
      <div className={`modal ${addModal.isOpen ? 'is-active' : ''}`}>
        <div className="modal-background" onClick={handleAddCancel}></div>
        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Add New Record</p>
            <button
              className="delete"
              aria-label="close"
              onClick={handleAddCancel}
            ></button>
          </header>
          <form onSubmit={handleAddSubmit}>
            <section className="modal-card-body">
              {tableData.length > 0 && Object.entries(tableData[0]).map(([key, value]) => {
                // Skip id and timestamp fields
                if (key === 'id' || key.includes('_at')) {
                  return null;
                }

                const fieldType = inferFieldType(key, value);
                const currentValue = addFormData[key] !== undefined ? addFormData[key] : '';

                return (
                  <div key={key} className="field">
                    <label className="label">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</label>
                    <div className="control">
                      {fieldType === 'boolean' ? (
                        <div className="field">
                          <div className="switch">
                            <input
                              id={`add-switch-${key}`}
                              type="checkbox"
                              checked={currentValue}
                              onChange={(e) => handleAddFormChange(key, e.target.checked)}
                            />
                            <label htmlFor={`add-switch-${key}`}>
                              {currentValue ? 'True' : 'False'}
                            </label>
                          </div>
                        </div>
                      ) : fieldType === 'textarea' ? (
                        <textarea
                          className="textarea"
                          value={currentValue}
                          onChange={(e) => handleAddFormChange(key, e.target.value)}
                          rows="4"
                          placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                        />
                      ) : fieldType === 'date' ? (
                        <input
                          className="input"
                          type="datetime-local"
                          value={currentValue}
                          onChange={(e) => handleAddFormChange(key, e.target.value)}
                        />
                      ) : fieldType === 'number' ? (
                        <input
                          className="input"
                          type="number"
                          value={currentValue}
                          onChange={(e) => handleAddFormChange(key, parseFloat(e.target.value) || 0)}
                          placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                        />
                      ) : (
                        <input
                          className="input"
                          type="text"
                          value={currentValue}
                          onChange={(e) => handleAddFormChange(key, e.target.value)}
                          placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </section>
            <footer className="modal-card-foot">
              <button
                className={`button is-success ${addLoading ? 'is-loading' : ''}`}
                type="submit"
                disabled={addLoading}
              >
                Add Record
              </button>
              <button
                className="button"
                type="button"
                onClick={handleAddCancel}
                disabled={addLoading}
              >
                Cancel
              </button>
            </footer>
          </form>
        </div>
      </div>

      {/* Delete Column Confirmation Modal */}
      <div className={`modal ${deleteColumnModal.isOpen ? 'is-active' : ''}`}>
        <div className="modal-background" onClick={handleDeleteColumnCancel}></div>
        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Delete Column</p>
            <button
              className="delete"
              aria-label="close"
              onClick={handleDeleteColumnCancel}
            ></button>
          </header>
          <section className="modal-card-body">
            <p>Are you sure you want to delete the column <strong>"{deleteColumnModal.columnName}"</strong> from table <strong>"{deleteColumnModal.tableName}"</strong>?</p>
            <div className="notification is-danger is-light">
              <strong>Warning:</strong> This will permanently delete the column and all its data. This action cannot be undone.
            </div>
            <div className="field">
              <label className="label">
                Type the column name <strong>"{deleteColumnModal.columnName}"</strong> to confirm:
              </label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  placeholder={`Type "${deleteColumnModal.columnName}" here`}
                  value={deleteColumnInput}
                  onChange={(e) => setDeleteColumnInput(e.target.value)}
                />
              </div>
            </div>
          </section>
          <footer className="modal-card-foot">
            <button
              className={`button is-danger ${deleteColumnLoading ? 'is-loading' : ''}`}
              onClick={handleDeleteColumnConfirm}
              disabled={deleteColumnLoading || deleteColumnInput !== deleteColumnModal.columnName}
            >
              Delete Column
            </button>
            <button
              className="button"
              onClick={handleDeleteColumnCancel}
              disabled={deleteColumnLoading}
            >
              Cancel
            </button>
          </footer>
        </div>
      </div>

      {/* Add Column Modal */}
      <div className={`modal ${addColumnModal.isOpen ? 'is-active' : ''}`}>
        <div className="modal-background" onClick={handleAddColumnCancel}></div>
        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Add column to {addColumnModal.tableName}</p>
            <button
              className="delete"
              aria-label="close"
              onClick={handleAddColumnCancel}
            ></button>
          </header>
          <form onSubmit={handleAddColumnSubmit}>
            <section className="modal-card-body">
              <div className="field">
                <label className="label">Column Name</label>
                <div className="control">
                  <input
                    className="input"
                    type="text"
                    placeholder="Enter column name"
                    value={addColumnFormData.columnName}
                    onChange={(e) => handleAddColumnFormChange('columnName', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="field">
                <label className="label">Default Value</label>
                <div className="field is-grouped">
                  <div className="control">
                    <label className="radio">
                      <input
                        type="radio"
                        name="columnType"
                        value="text"
                        checked={addColumnFormData.columnType === 'text'}
                        onChange={(e) => handleAddColumnFormChange('columnType', e.target.value)}
                      />
                      <span className="ml-2">Text</span>
                    </label>
                  </div>
                  <div className="control">
                    <label className="radio">
                      <input
                        type="radio"
                        name="columnType"
                        value="textarea"
                        checked={addColumnFormData.columnType === 'textarea'}
                        onChange={(e) => handleAddColumnFormChange('columnType', e.target.value)}
                      />
                      <span className="ml-2">Text Area</span>
                    </label>
                  </div>
                  <div className="control">
                    <label className="radio">
                      <input
                        type="radio"
                        name="columnType"
                        value="number"
                        checked={addColumnFormData.columnType === 'number'}
                        onChange={(e) => handleAddColumnFormChange('columnType', e.target.value)}
                      />
                      <span className="ml-2">Number</span>
                    </label>
                  </div>
                  <div className="control">
                    <label className="radio">
                      <input
                        type="radio"
                        name="columnType"
                        value="date"
                        checked={addColumnFormData.columnType === 'date'}
                        onChange={(e) => handleAddColumnFormChange('columnType', e.target.value)}
                      />
                      <span className="ml-2">Date Time</span>
                    </label>
                  </div>
                  <div className="control">
                    <label className="radio">
                      <input
                        type="radio"
                        name="columnType"
                        value="boolean"
                        checked={addColumnFormData.columnType === 'boolean'}
                        onChange={(e) => handleAddColumnFormChange('columnType', e.target.value)}
                      />
                      <span className="ml-2">Boolean</span>
                    </label>
                  </div>
                </div>
                <div className="control mt-3">
                  {addColumnFormData.columnType === 'textarea' ? (
                    <textarea
                      className="textarea"
                      placeholder="Enter default value (optional)"
                      value={addColumnFormData.defaultValue}
                      onChange={(e) => handleAddColumnFormChange('defaultValue', e.target.value)}
                      rows="4"
                    />
                  ) : addColumnFormData.columnType === 'boolean' ? (
                    <div className="select">
                      <select
                        value={addColumnFormData.defaultValue}
                        onChange={(e) => handleAddColumnFormChange('defaultValue', e.target.value)}
                      >
                        <option value="">No default</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </select>
                    </div>
                  ) : addColumnFormData.columnType === 'date' ? (
                    <input
                      className="input"
                      type="datetime-local"
                      value={addColumnFormData.defaultValue}
                      onChange={(e) => handleAddColumnFormChange('defaultValue', e.target.value)}
                    />
                  ) : (
                    <input
                      className="input"
                      type={addColumnFormData.columnType === 'number' ? 'number' : 'text'}
                      placeholder="Enter default value (optional)"
                      value={addColumnFormData.defaultValue}
                      onChange={(e) => handleAddColumnFormChange('defaultValue', e.target.value)}
                    />
                  )}
                </div>
              </div>
            </section>
            <footer className="modal-card-foot">
              <button
                className={`button is-success ${addColumnLoading ? 'is-loading' : ''}`}
                type="submit"
                disabled={addColumnLoading || !addColumnFormData.columnName.trim()}
              >
                Create
              </button>
              <button
                className="button"
                type="button"
                onClick={handleAddColumnCancel}
                disabled={addColumnLoading}
              >
                Cancel
              </button>
            </footer>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default App;
